import { collection, getDocs, query, where, orderBy, limit, getCountFromServer, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase/firebaseConfig';
import { Student, DashboardStats } from '../types/student';

const COLLECTIONS = {
  students: 'students',
  coaches: 'coaches',
  groups: 'groups'
};

export class DashboardService {
  static async getCoachesCount(): Promise<number> {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.coaches));
    return querySnapshot.size;
  }

  static async getStudentsCount(): Promise<number> {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.students));
    return querySnapshot.size;
  }

  static async getActiveStudentCount(): Promise<number> {
    const q = query(
      collection(db, COLLECTIONS.students),
      where('deleted', '==', false)
    );
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }

  static async getGroupsCount(): Promise<number> {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.groups));
    return querySnapshot.size;
  }

  static async getRecentStudents(): Promise<Student[]> {
    const q = query(
      collection(db, COLLECTIONS.students),
      orderBy('createdAt', 'desc'),
      limit(5)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    })) as Student[];
  }

  static async getAllStudents(limitCount: number = 10, startAfter?: any): Promise<{students: Student[], hasMore: boolean}> {
    let q = query(
      collection(db, COLLECTIONS.students),
      orderBy('createdAt', 'desc'),
      limit(limitCount + 1) // Get one extra to check if there are more
    );

    if (startAfter) {
      q = query(q, startAfter(startAfter));
    }

    const querySnapshot = await getDocs(q);
    const students = querySnapshot.docs.slice(0, limitCount).map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    })) as Student[];

    const hasMore = querySnapshot.docs.length > limitCount;

    return { students, hasMore };
  }

  static async getDashboardStats(): Promise<DashboardStats> {
    const [totalStudents, activeStudents, totalCoaches, totalGroups] = await Promise.all([
      this.getStudentsCount(),
      this.getActiveStudentCount(),
      this.getCoachesCount(),
      this.getGroupsCount()
    ]);

    return {
      totalStudents,
      activeStudents,
      totalCoaches,
      totalGroups
    };
  }

  static async updateStudent(student: Student): Promise<void> {
    try {
      const studentRef = doc(db, COLLECTIONS.students, student.id);
      await updateDoc(studentRef, {
        fullName: student.fullName,
        schoolName: student.schoolName,
        schoolId: student.schoolId,
        coachId: student.coachId,
        deleted: student.deleted,
      });
    } catch (error) {
      console.error('Error updating student in Firestore:', error);
      throw new Error(`Failed to update student: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}